import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, type Anomaly, type AnomalyDecision } from "../lib/api";

/**
 * Settings → Anomaly review: the unusual events the flight data or the plan has thrown up,
 * what the system does about each now, and a duty manager's word on how it should. A
 * decision is a record for the people who build the system; it changes nothing by itself.
 */

const card = "rounded-xl border border-line p-4";
const btn = "rounded-lg border border-line px-2.5 py-1 text-[12.5px] font-semibold hover:bg-panel-2 disabled:opacity-40";

const DECISIONS: Array<[AnomalyDecision, string]> = [
  ["keep", "Keep how it works now"],
  ["change", "Change it as proposed"],
  ["other", "Something else (say how in the note)"],
];

export function AnomalySection({ tz }: { tz: string }) {
  const q = useQuery({ queryKey: ["anomalies"], queryFn: api.anomalies, refetchInterval: 60_000 });
  const list = q.data?.anomalies ?? [];
  const waiting = list.filter((a) => !a.decision).length;
  return (
    <>
      <h2 className="mb-1 text-[18px] font-bold">Anomaly review</h2>
      <p className="mb-4 max-w-3xl text-[12.5px] text-ink-3">
        Unusual events the flight data or the plan has produced, each written up once with what the system does about it now. The ones marked <b>Watched</b> are logged
        automatically every time they happen again. A duty manager reads each one and records how the system should handle it; the decision is kept with their name and
        goes to the people who build the system. It does not change anything on its own.
      </p>
      {q.isLoading && <Loader2 className="size-4 animate-spin text-ink-3" />}
      {q.data && <p className="mb-3 text-[12.5px] font-semibold">{waiting ? `${waiting} of ${list.length} waiting for a duty manager` : `All ${list.length} reviewed`}</p>}
      <div className="flex flex-col gap-3">
        {list.map((a) => (
          <AnomalyCard key={a.kind} a={a} tz={tz} />
        ))}
      </div>
    </>
  );
}

function AnomalyCard({ a, tz }: { a: Anomaly; tz: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(!a.decision);
  const [decision, setDecision] = useState<AnomalyDecision | null>(a.decision?.decision ?? null);
  const [note, setNote] = useState(a.decision?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.decideAnomaly(a.kind, decision!, note),
    onSuccess: (r) => {
      setError(null);
      qc.setQueryData(["anomalies"], r);
    },
    onError: (e) => setError((e as Error).message),
  });
  const status = a.decision
    ? { text: a.decision.decision === "keep" ? "Confirmed as it is" : a.decision.decision === "change" ? "Change requested" : "Other handling requested", tone: a.decision.decision === "keep" ? "bg-ok/15 text-ok" : "bg-info/15 text-info" }
    : { text: "Waiting for a duty manager", tone: "bg-warn/15 text-warn" };
  const when = (iso: string) => DateTime.fromISO(iso).setZone(tz).toFormat("d LLL HH:mm");

  return (
    <div className={card}>
      <button className="flex w-full flex-wrap items-center gap-2 text-left" onClick={() => setOpen((v) => !v)}>
        <span className="text-[14px] font-bold">{a.title}</span>
        <span className={clsx("rounded-full px-2 py-0.5 text-[11px] font-semibold", status.tone)}>{status.text}</span>
        {a.watched && <span className="rounded-full bg-panel-2 px-2 py-0.5 text-[11px] text-ink-2">{`Watched · ${a.recent} in the last 30 days`}</span>}
      </button>
      {open && (
        <div className="mt-2 grid gap-2 text-[12.5px]">
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">What happened</div>
            <div>{a.seen}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">What the system does now</div>
            <div>{a.now}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Question for the duty manager</div>
            <div className="font-semibold">{a.question}</div>
            <div className="mt-0.5 text-ink-2">{`Proposed: ${a.proposal}`}</div>
          </div>

          {a.events.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Seen since</div>
              <table className="mt-1 w-full max-w-3xl text-[12px]">
                <tbody>
                  {a.events.map((e) => (
                    <tr key={e.id} className="border-b border-line">
                      <td className="tabular px-1 py-1 whitespace-nowrap text-ink-3">{DateTime.fromISO(e.date).toFormat("ccc d LLL")}</td>
                      <td className="px-1 py-1 font-semibold">{e.flights}</td>
                      <td className="px-1 py-1">{e.detail}</td>
                      <td className="tabular px-1 py-1 whitespace-nowrap text-ink-3">{e.count > 1 ? `seen ${e.count}× until ${when(e.lastSeen)}` : when(e.firstSeen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="rounded-lg bg-panel-2 p-2.5">
            <div className="mb-1 text-[12px] font-semibold">How should the system handle it?</div>
            <div className="flex flex-col gap-1">
              {DECISIONS.map(([d, label]) => (
                <label key={d} className="flex items-center gap-2">
                  <input type="radio" name={`anomaly-${a.kind}`} checked={decision === d} onChange={() => setDecision(d)} />
                  {label}
                </label>
              ))}
            </div>
            <textarea
              className="mt-2 w-full rounded-md border border-line bg-panel px-2 py-1 text-[12.5px]"
              rows={2}
              maxLength={1000}
              placeholder="Note for the team: why, exceptions, who to ask"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <button className={btn} disabled={!decision || save.isPending} onClick={() => save.mutate()}>
                {a.decision ? "Update the decision" : "Record the decision"}
              </button>
              {a.decision && <span className="text-[11.5px] text-ink-3">{`Last decided by ${a.decision.by}, ${when(a.decision.at)}`}</span>}
              {error && <span className="text-[12px] text-bad">{error}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
